/**
 * CR283: Merchant Audit Log Card Component
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { History } from "lucide-react";
import { format } from "date-fns";
import type { AuditLogEntry } from "@/hooks/useMerchantsDirectory";

interface MerchantAuditLogCardProps {
  auditLog: AuditLogEntry[];
  loading: boolean;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  status_change: "Status Change",
  scan_toggle: "Scan Toggle",
  alias_add: "Alias Added",
  alias_delete: "Alias Deleted",
  legacy_resync: "Legacy Resync",
  merchant_update: "Overview Update",
  branding_update: "Branding Update",
};

const EVENT_TYPE_VARIANTS: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  status_change: "default",
  scan_toggle: "secondary",
  alias_add: "outline",
  alias_delete: "destructive",
  legacy_resync: "secondary",
  merchant_update: "outline",
  branding_update: "outline",
};

export function MerchantAuditLogCard({ auditLog, loading }: MerchantAuditLogCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Audit Log
        </CardTitle>
        <CardDescription>Recent changes to this merchant (last 25).</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : auditLog.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">No audit entries yet.</div>
        ) : (
          <ScrollArea className="h-[300px]">
            <div className="space-y-3">
              {auditLog.map((entry, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={EVENT_TYPE_VARIANTS[entry.event_type] || "outline"}>
                        {EVENT_TYPE_LABELS[entry.event_type] || entry.event_type}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(entry.created_at), "MMM d, yyyy HH:mm")}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {entry.actor_email || entry.actor_user_id || "System"}
                    </div>
                    {entry.details && Object.keys(entry.details).length > 0 && (
                      <pre className="text-xs font-mono bg-background p-2 rounded mt-2 overflow-x-auto">
                        {JSON.stringify(entry.details, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
